import {IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SearchLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {peopleSearchRecentsAtom} from 'atoms/workstreams';
import cx from 'classnames';
import {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {useSearch} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useAtom, useAtomValue} from 'jotai';
import {useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {workstreamsPersonPath} from 'views/workstreams/page/workstreamsRoutes';

import {SidebarOverflowTooltip} from './SidebarOverflowTooltip';
import styles from './WorkstreamSidebar.module.css';

export type PersonOption = {
  user_id: string;
  ldap: string;
  name: string;
  profile_photo_url?: string | null;
};

interface PeopleSearchProps {
  isNested?: boolean;
  /** When true, render only a search icon button (e.g. in People header); clicking opens the search menu. */
  renderAsIconButton?: boolean;
  /** Optional surface-specific class for the full-width navigation trigger. */
  navItemClassName?: string;
  /** Override Workstreams navigation when the search is reused by another surface. */
  onSelectPerson?: (employee: PersonOption) => void;
  /** Override the active result when the search is reused outside Workstreams. */
  selectedPersonLdap?: string | null;
}

const PEOPLE_SEARCH_MENU_Z_INDEX = 20;

export const PeopleSearch = ({
  isNested = true,
  renderAsIconButton = false,
  navItemClassName,
  onSelectPerson,
  selectedPersonLdap,
}: PeopleSearchProps) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [searchRecents, setSearchRecents] = useAtom(peopleSearchRecentsAtom);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const {reportingLine, directReports} = useAtomValue(loggedInEmployeeAtom);

  const searchResults = useSearch({input: inputValue});

  // Build suggestions from reporting line when no search input
  const suggestions = useMemo<PersonOption[]>(() => {
    const [, manager, ...orgLine] = reportingLine;
    const people = [manager, ...directReports, ...orgLine].filter(Boolean) as Employee[];
    // Dedupe by user_id
    const seen = new Set<string>();
    return people.filter((p) => {
      if (seen.has(p.user_id)) return false;
      seen.add(p.user_id);
      return true;
    });
  }, [reportingLine, directReports]);

  const handleSelection = (employee: PersonOption, closeMenu: () => void) => {
    setSearchRecents((prev) => {
      const deduped = prev.filter((item) => item.ldap !== employee.ldap);
      return [
        {
          user_id: employee.user_id,
          ldap: employee.ldap,
          name: employee.name,
          profile_photo_url: employee.profile_photo_url ?? null,
        },
        ...deduped,
      ].slice(0, 10);
    });

    if (onSelectPerson) {
      onSelectPerson(employee);
    } else {
      navigate(workstreamsPersonPath(employee.ldap), {
        state: {
          source: 'people_search',
        },
      });
    }
    setInputValue('');
    closeMenu();
  };

  // Determine which results to show
  const isSearching = inputValue.length > 0;
  const hasRecentSelections = searchRecents.length > 0;
  const results: PersonOption[] = isSearching
    ? searchResults
    : hasRecentSelections
      ? searchRecents
      : suggestions;
  const sectionLabel = isSearching ? null : hasRecentSelections ? t('recent') : t('suggested');

  return (
    <Menu.Wrapper
      isPortaled
      style={{display: 'block', width: renderAsIconButton ? undefined : '100%'}}
      onToggle={({isOpen: open}) => {
        setIsMenuOpen(open);
        if (open) {
          setTimeout(() => {
            inputWrapperRef.current?.querySelector('input')?.focus();
          }, 0);
        } else {
          setInputValue('');
        }
      }}
    >
      {({getTriggerProps, getContentProps, closeMenu}) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            {renderAsIconButton ? (
              <span onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Tooltip title={t('search_people_tooltip')} placement="top">
                  <span>
                    <IconButton
                      variant="transparent"
                      size="small"
                      className={styles.sectionHeaderAction}
                      {...triggerProps}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        triggerProps.onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
                      }}
                      aria-label="icon button"
                    >
                      <UIIcon isOpticallyAligned src={SearchLine} size="medium" />
                    </IconButton>
                  </span>
                </Tooltip>
              </span>
            ) : (
              /* Search button styled as nav item */
              <Box
                {...triggerProps}
                {...withShade({
                  className: cx(styles.navItem, styles.searchNavItem, navItemClassName, {
                    [styles.active]: isMenuOpen,
                  }),
                })}
              >
                <div className={styles.navItemContent}>
                  {isNested ? (
                    <span className={styles.peopleNestedLeading}>
                      <UIIcon isOpticallyAligned src={SearchLine} size="medium" />
                    </span>
                  ) : (
                    <UIIcon isOpticallyAligned src={SearchLine} size="medium" />
                  )}
                  <SidebarOverflowTooltip
                    className={styles.navItemLabel}
                    color="subtle"
                    text={t('search_people')}
                  />
                </div>
              </Box>
            )}
            {/* Search portal */}
            <Menu.Content
              {...getContentProps()}
              placement="right-start"
              style={{width: 280, zIndex: PEOPLE_SEARCH_MENU_Z_INDEX}}
            >
              <Stack gap="4">
                {/* Search input */}
                <Box paddingX="12" paddingTop="12" paddingBottom="8">
                  <div ref={inputWrapperRef}>
                    <TextInput.Container
                      size="medium"
                      className={styles.peopleSearchInput}
                      style={{borderColor: 'var(--dig-color__border__subtle)'}}
                    >
                      <TextInput.Accessory>
                        <UIIcon isOpticallyAligned src={SearchLine} size="medium" />
                      </TextInput.Accessory>
                      <TextInput.Input
                        placeholder={t('search_people')}
                        value={inputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInputValue(e.currentTarget.value)
                        }
                      />
                    </TextInput.Container>
                  </div>
                </Box>

                {/* Results section */}
                <Stack gap="0" paddingBottom="8">
                  {/* Section header */}
                  {results.length > 0 && sectionLabel && (
                    <Box paddingX="16" paddingY="4">
                      <Text size="small" color="subtle">
                        {sectionLabel}
                      </Text>
                    </Box>
                  )}

                  {/* Results list */}
                  {results.slice(0, 10).map((employee) => (
                    <Box
                      key={employee.user_id}
                      {...withShade({
                        className: cx(styles.searchResultItem, {
                          [styles.searchResultItemActive]: selectedPersonLdap === employee.ldap,
                        }),
                      })}
                      onClick={() => handleSelection(employee, () => closeMenu({}))}
                    >
                      <Avatar user={employee} size="small" />
                      <Text>{employee.name}</Text>
                    </Box>
                  ))}

                  {/* No results message */}
                  {isSearching && results.length === 0 && (
                    <Box paddingX="16" paddingY="8">
                      <Text color="subtle">{t('no_people_found')}</Text>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
